import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export type DocuSignEvent =
  | 'signing_complete'
  | 'send'
  | 'cancel'
  | 'decline'
  | 'exception'
  | 'fax_pending'
  | 'session_timeout'
  | 'save'
  | 'ttl_expired'

const DOCUSIGN_EVENTS = new Set<DocuSignEvent>([
  'signing_complete',
  'send',
  'cancel',
  'decline',
  'exception',
  'fax_pending',
  'session_timeout',
  'save',
  'ttl_expired',
])

const isDocuSignEvent = (value: string | null | undefined): value is DocuSignEvent =>
  !!value && DOCUSIGN_EVENTS.has(value as DocuSignEvent)

interface DocuSignEmbeddedViewProps {
  signingUrl: string | null
  title: string
  onComplete: (event: DocuSignEvent) => void
  onCancel: () => void
  onError: (error: string) => void
}

const Container = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.backgroundPrimary,
  display: 'flex',
  flexDirection: 'column',
}))

const Header = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing['16px'],
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
}))

const IframeContainer = styled.View({
  flex: 1,
  position: 'relative',
})

const LoadingOverlay = styled.View(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundPrimary,
  zIndex: 1,
}))

const ButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

export const DocuSignEmbeddedView: React.FC<DocuSignEmbeddedViewProps> = ({
  signingUrl,
  title,
  onComplete,
  onCancel,
  onError,
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const hasCompletedRef = useRef(false)

  // Handle postMessage from DocuSign callback page (fires when return_url page posts a message)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'docusign-callback') return
      if (hasCompletedRef.current) return

      if (!isDocuSignEvent(event.data.event)) return
      hasCompletedRef.current = true
      onComplete(event.data.event)
    },
    [onComplete],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  const handleIframeLoad = () => {
    setIsLoading(false)

    // When DocuSign redirects the iframe to our return_url, it becomes same-origin.
    // We can then read the location to extract the DocuSign event query parameter.
    try {
      const iframeLocation = iframeRef.current?.contentWindow?.location
      if (iframeLocation && iframeLocation.origin === window.location.origin) {
        const params = new URLSearchParams(iframeLocation.search)
        const docuSignEvent = params.get('event')?.toLowerCase()
        if (isDocuSignEvent(docuSignEvent) && !hasCompletedRef.current) {
          hasCompletedRef.current = true
          onComplete(docuSignEvent)
        }
      }
    } catch {
      // SecurityError is expected while the iframe is on DocuSign's cross-origin domain
    }
  }

  const handleIframeError = () => {
    setIsLoading(false)
    onError('Failed to load DocuSign signing view')
  }

  return (
    <Container testID={tID('DocuSignEmbeddedView')}>
      <Header>
        <BodyText text={title} color={colors.textPrimary} />
        <ButtonRow>
          <SecondaryButton
            text={intl.formatMessage({ defaultMessage: 'Cancel', description: 'Cancel button' })}
            onPress={() => {
              if (hasCompletedRef.current) return
              hasCompletedRef.current = true
              onCancel()
            }}
            testID={tID('DocuSignEmbeddedView-cancel')}
          />
        </ButtonRow>
      </Header>
      <IframeContainer>
        {(isLoading || !signingUrl) && (
          <LoadingOverlay>
            <LoadingIndicator topPadding={0} />
          </LoadingOverlay>
        )}
        {signingUrl && (
          <iframe
            ref={iframeRef}
            src={signingUrl}
            title={intl.formatMessage({
              defaultMessage: 'DocuSign signature setup',
              description: 'Title for DocuSign iframe',
            })}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            data-testid={tID('DocuSignEmbeddedView-iframe')}
          />
        )}
      </IframeContainer>
    </Container>
  )
}

export default DocuSignEmbeddedView
